import activityIndicatorIcon from "@ui/icons/activityIndicator.svg?raw";
import type { Component, JSX } from "solid-js";
import { createEffect, splitProps } from "solid-js";
import { resolveCssColor } from "~/modules/shared/utils/resolveCssColor";
import "./Icon.css";

type Props = {
	icon: string;
	/** CSS color, `var(--…)`, or token shorthand e.g. `grey-600`, `gvt-blue-500`, `primary` */
	fill?: string;
	width?: string;
	height?: string;
	class?: string;
	classList?: Record<string, boolean | undefined>;
	dataType?: string;
	hasActivityIndicator?: boolean;
	fillWithCurrentColor?: boolean;
	/**
	 * With `fillWithCurrentColor`, paint only the white mark of a two-tone icon
	 * and hide its tile. Use when the icon sits on a chip that is already the
	 * brand colour, where a recoloured tile would cover the mark.
	 */
	glyphOnly?: boolean;
	style?: JSX.CSSProperties;
	/**
	 * - To get the icon to flow inside text, set to "contents".
	 * - If you want to use the icon as a standalone element, e.g. in a flex container, set to "inline-flex".
	 * @default contents
	 */
	display?: "contents" | "inline-flex";
};

/** Instagram / Facebook (and similar) draw inner details as a white path. */
const isWhiteFill = (value: string | null) => {
	if (!value) {
		return false;
	}
	const fill = value.trim().toLowerCase().replace(/\s+/g, "");
	return (
		fill === "white" ||
		fill === "#fff" ||
		fill === "#ffffff" ||
		fill === "rgb(255,255,255)"
	);
};

/**
 * Renders SVG icons from raw SVG strings with optional size and fill.
 *
 * [Design System →](http://localhost:5174/component/Icon)
 * @deprecated Use MaterialSymbol instead
 */
export const Icon: Component<Props> = (props) => {
	const [local, rest] = splitProps(props, [
		"icon",
		"fill",
		"width",
		"height",
		"class",
		"dataType",
		"hasActivityIndicator",
		"fillWithCurrentColor",
		"glyphOnly",
		"style",
		"display",
	]);
	let containerRef: HTMLSpanElement | undefined;

	const paint = () => {
		const el = containerRef;
		if (!el) return;

		el.innerHTML = local.hasActivityIndicator
			? activityIndicatorIcon + local.icon
			: local.icon;

		const svgs = el.getElementsByTagName("svg");
		const targetSvg = local.hasActivityIndicator ? svgs[1] : svgs[0];
		const fillWithCurrentColor = local.fillWithCurrentColor ?? true;

		if (targetSvg) {
			if (local.width) {
				targetSvg.setAttribute("width", local.width);
				targetSvg.style.width = local.width;
			} else {
				targetSvg.style.removeProperty("width");
			}
			if (local.height) {
				targetSvg.setAttribute("height", local.height);
				targetSvg.style.height = local.height;
			} else {
				targetSvg.style.removeProperty("height");
			}

			if (fillWithCurrentColor) {
				const fill = resolveCssColor(local.fill ?? "currentColor");
				const paths = Array.from(targetSvg.getElementsByTagName("path"));
				const marks = paths.filter((path) =>
					isWhiteFill(path.getAttribute("fill")),
				);
				const paintMarksOnly = local.glyphOnly && marks.length > 0;
				paths.forEach((path) => {
					const isMark = marks.includes(path);
					if (paintMarksOnly) {
						path.setAttribute("fill", isMark ? fill : "none");
						return;
					}
					if (!isMark) {
						path.setAttribute("fill", fill);
					}
				});
			}

			if (local.hasActivityIndicator) {
				targetSvg.style.width = "80%";
				targetSvg.style.height = "80%";
				targetSvg.style.top = "10%";
				targetSvg.style.left = "10%";
			}
		}
	};

	createEffect(() => {
		// Track icon-related props so updates re-paint
		local.icon;
		local.hasActivityIndicator;
		local.fillWithCurrentColor;
		local.glyphOnly;
		local.fill;
		local.width;
		local.height;
		paint();
	});

	return (
		<span
			ref={(el) => {
				containerRef = el ?? undefined;
				if (el) paint();
			}}
			class={`gvtui-icon ${local.hasActivityIndicator ? "gvtui-icon-stacked" : ""} ${local.class ?? ""}`}
			classList={props.classList}
			style={{
				width:
					local.width ?? (local.hasActivityIndicator ? "1.2rem" : undefined),
				height:
					local.height ?? (local.hasActivityIndicator ? "1.2rem" : undefined),
				display: local.display ?? "contents",
				...local.style,
			}}
			data-type={local.dataType}
			{...rest}
		/>
	);
};
